import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { IdType, SortingRule } from "react-table";
import {
  HvButton,
  HvGrid,
  HvTypography,
} from "@hitachivantara/uikit-react-core";
import {
  HvTableColumnConfig,
  HvCellProps,
  useHvTable,
  useHvBulkActions,
  useHvPagination,
  useHvRowSelection,
  useHvSortBy,
} from "@hitachivantara/uikit-react-lab";
import { Edit, Archive } from "@hitachivantara/uikit-react-icons";

import { archiveIncidentType, IconData, useIncidentTypes } from "lib/api";
import { NotificationContext } from "lib/context/NotificationContext";
import { useSnackbar } from "lib/provider/SnackbarProvider";
import { numberFallback } from "lib/utils/fallbacks";
import SimpleTable, {
  textColumn,
  actionsColumn,
  textWithIconColumn,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
} from "components/common/SimpleTable";
import ArchiveDialog from "components/common/ArchiveDialog";
import IncidentTypeTableControls from "../IncidentTypeTableControls";

interface IncidentTypeDataRow extends Record<string, unknown> {
  id: number;
  name: string;
  icon: IconData;
  category?: string;
  subCategory?: string;
  description: string;
  incidentCount: number;
}

const sortFieldMap = {
  name: "inc_type_name",
  category: "category.cat_name",
  subCategory: "subcategory.sub_cat_name",
};

const DEFAULT_SORT = [
  {
    id: "name",
    desc: false,
  },
];

const IncidentTypeTable: React.FC = () => {
  const { t } = useTranslation("incidentType");
  const history = useHistory();

  const [tableData, setTableData] = useState<IncidentTypeDataRow[]>([]);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [totalCount, setTotalCount] = useState(0);

  const { setShowBanner, setBannerMessage } = useContext(NotificationContext);

  const columns = useMemo(
    (): HvTableColumnConfig<IncidentTypeDataRow>[] => [
      textWithIconColumn(
        {
          Header: t("columns.incidentTypeName") as string,
          accessor: "name",
          style: { width: "20%" },
          sortType: "String",
        },
        "icon"
      ),
      textColumn({
        Header: t("columns.incidentTypeCategory") as string,
        accessor: "category",
        style: { width: "15%" },
      }),
      textColumn({
        Header: t("columns.incidentTypeSubcategory") as string,
        accessor: "subCategory",
        style: { width: "15%" },
      }),
      textColumn({
        Header: t("columns.incidentTypeDescription") as string,
        accessor: "description",
        disableSortBy: true,
      }),
      {
        Header: t("columns.incidentsCount") as string,
        accessor: "incidentCount",
        align: "right",
        disableSortBy: true,
        style: { width: 100 },
        Cell: ({ row, value }: HvCellProps<IncidentTypeDataRow>) => {
          return (
            <HvTypography
              variant="link"
              component="a"
              onClick={() =>
                history.push(
                  `/settings/incident?incidentType=${row.original.id}`
                )
              }
              style={{ cursor: "pointer" }}
            >
              {numberFallback(value)}
            </HvTypography>
          );
        },
      },
      actionsColumn({
        id: "edit",
        Cell: ({ row }: HvCellProps<IncidentTypeDataRow>) => {
          return (
            <HvButton
              icon
              category="ghost"
              aria-label={t("labels.editIncidentTypeButton")}
              onClick={() =>
                history.push(`/settings/incident-type/${row.original.id}`)
              }
            >
              <Edit />
            </HvButton>
          );
        },
      }),
    ],
    [t, history]
  );

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] =
    useState<SortingRule<IncidentTypeDataRow>[]>(DEFAULT_SORT);
  const sortField = sortFieldMap[sortBy[0].id as keyof typeof sortFieldMap];
  const [searchKey, setSearchKey] = useState("");
  const [filter, setFilter] = useState<Record<string, unknown>>({});

  const {
    data: incidentTypesData,
    error: incidentTypesError,
    mutate,
  } = useIncidentTypes(
    {
      pageNr: page + 1,
      pageSize: size,
      orderBy: encodeURIComponent(sortField),
      Order: sortBy[0].desc ? "desc" : "asc",
      SearchKey: searchKey,
    },
    { suspense: tableData.length === 0 },
    filter,
    true
  );

  const [selectedIds, setSelectedIds] = useState<
    Record<IdType<IncidentTypeDataRow>, boolean>
  >({});

  useEffect(() => {
    if (incidentTypesData) {
      setTableData(
        incidentTypesData.data.map((incidentType) => ({
          id: incidentType.pk_inc_type_id,
          name: incidentType.inc_type_name,
          icon: incidentType.appearance,
          category: incidentType.category?.cat_name,
          subCategory: incidentType.subcategory?.sub_cat_name,
          description: incidentType.inc_type_desc,
          incidentCount: incidentType.incident_count,
        }))
      );
      setTotalCount(incidentTypesData.totalCount);
    }
  }, [incidentTypesData]);

  const initialState = useMemo(() => {
    const entries = tableData.map<[string, boolean]>((v) => [
      v.id.toString(),
      v.incidentCount > 0,
    ]);

    return {
      pageIndex: page,
      pageSize: size,
      lockedSelectionRowIds: (entries && Object.fromEntries(entries)) ?? {},
      selectedRowIds: selectedIds,
      sortBy,
      searchKey,
    };
  }, [tableData, page, size, selectedIds, sortBy, searchKey]);

  const labels = useMemo(
    () => ({
      selectRowCheckBoxAriaLabel: t("labels.selectIncidentTypeCheckbox"),
    }),
    [t]
  );

  const bulkActions = useMemo(
    () => [
      {
        id: "archive",
        label: t("labels.bulkArchiveIncidentTypeButton"),
        icon: <Archive />,
        onClick: () => setIsArchiveDialogOpen(true),
      },
    ],
    [t]
  );

  const instance = useHvTable<IncidentTypeDataRow>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback((v: IncidentTypeDataRow): string => `${v.id}`, []),
      manualPagination: true,
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      initialState,
      labels,
      aditivePageBulkSelection: true,
      subtractivePageBulkDeselection: true,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvSortBy,
    useHvPagination,
    useHvRowSelection,
    useHvBulkActions
  );

  const {
    state: { selectedRowIds, pageIndex, pageSize, sortBy: sortByFromTable },
  } = instance;

  useEffect(() => setPage?.(pageIndex), [pageIndex, setPage]);
  useEffect(() => setSize?.(pageSize), [pageSize, setSize]);
  useEffect(() => {
    setSortBy?.(sortByFromTable);
    setPage?.(0);
  }, [sortByFromTable, setSortBy, setPage]);
  useEffect(() => setSelectedIds(selectedRowIds), [selectedRowIds]);

  const onSearchHandler = (value: string) => {
    setSearchKey(value);
    instance.gotoPage(DEFAULT_PAGE);
  };

  const onFilterHandler = (value: string[][]) => {
    setFilter({
      category: {
        filterValues: value[0] || [],
      },
      subCategory: {
        filterValues: value[1] || [],
      },
    });
    instance.gotoPage(DEFAULT_PAGE);
  };

  const onArchiveClose = useCallback(
    (result, message, ids) => {
      setIsArchiveDialogOpen(false);

      if (ids) {
        const newSelectedIds = {
          ...selectedIds,
        };

        ids.forEach((id: string) => {
          delete newSelectedIds[id];
        });
        setSelectedIds(newSelectedIds);
      }

      if (result && ids) {
        enqueueSnackbar(message, {
          variant: "success",
        });
        setPage(DEFAULT_PAGE);
        mutate();
      } else if (message) {
        setBannerMessage(message);
        setShowBanner(true);
        mutate();
      }
    },
    [enqueueSnackbar, mutate, selectedIds, setBannerMessage, setShowBanner]
  );

  return (
    <HvGrid container spacing={2}>
      <HvGrid item xs={12}>
        <IncidentTypeTableControls
          onSearch={onSearchHandler}
          onFilter={onFilterHandler}
        />
      </HvGrid>
      <HvGrid item xs={12}>
        <ArchiveDialog
          scope="incidentType"
          isOpen={isArchiveDialogOpen}
          promise={archiveIncidentType}
          onClose={onArchiveClose}
          ids={Object.keys(selectedRowIds)}
        />
        <SimpleTable
          instance={instance}
          bulkActions={bulkActions}
          totalCount={totalCount}
          loading={
            (!incidentTypesData && !incidentTypesError) || tableData === []
          }
        />
      </HvGrid>
    </HvGrid>
  );
};

export default IncidentTypeTable;
